/**
 * @namespace tk
 * @namespace serverProfile
 */
import $ from 'jquery';
import validate from 'validate';
import Handlebars from 'handlebars';
import moment from 'moment';
import Vue from 'vue';
import Vuex from 'vuex';
import pako from 'pako';
import AnalyticsPlugin from 'p/AnalyticsPlugin';
import MediaQueryPlugin from 'p/MediaQueryPlugin';
import Analytics from 'c/WtAnalytics';
import api from 'api/api';
import {
  AutoCropHandler,
  CleanScaleAndOffsetHandler,
} from '@wochitnpm/adjustasset';
import LiquorTree from 'liquor-tree';
import Marionette from 'backbone.marionette';
import app from '#/app';
import package_json from '../../package.json';

const Application = Marionette.Application.extend({
  version: package_json.version,

  isEmbedded: (function () {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  })(),

  /**
   * namespace placeholder for relevant classes
   * @type {Object}
   */
  namespace: {
    Controller: {},
    Vue: {},
    View: {},
    Model: {},
    Collection: {},
    Navigation: {},
    Behavior: {},
    Util: {},
  },

  /**
   * Get url to server api
   * @param path {string}
   * @param [objUrlParams] {object}
   * @return {string}
   */
  apiUrl: function (path, objUrlParams) {
    let sUrlParams = '';
    if (!_.isEmpty(objUrlParams)) {
      if (/\?/.test(path)) {
        if (!/\?$/.test(path)) {
          sUrlParams += '&';
        }
      } else {
        sUrlParams += '?';
      }
      sUrlParams += $.param(objUrlParams);
    }

    return serverProfile.apiUrl + path + sUrlParams;
  },

  /**
   * Get url to server api
   * @param path {string}
   * @param [objUrlParams] {object}
   * @return {string}
   */
  authUrl: function (path, objUrlParams) {
    let sUrlParams = '';
    if (!_.isEmpty(objUrlParams)) {
      if (/\?/.test(path)) {
        if (!/\?$/.test(path)) {
          sUrlParams += '&';
        }
      } else {
        sUrlParams += '?';
      }
      sUrlParams += $.param(objUrlParams);
    }

    return serverProfile.authUrl + path + sUrlParams;
  },

  /**
   * Get url to server api
   * @param path {string}
   * @param [objUrlParams] {object}
   * @return {string}
   */
  renderApiUrl: function (path, objUrlParams) {
    let sUrlParams = '';
    if (!_.isEmpty(objUrlParams)) {
      if (/\?/.test(path)) {
        if (!/\?$/.test(path)) {
          sUrlParams += '&';
        }
      } else {
        sUrlParams += '?';
      }
      sUrlParams += $.param(objUrlParams);
    }

    return serverProfile.renderApiUrl + path + sUrlParams;
  },

  /**
   * Get url to AI api
   * @param path {string}
   * @param [objUrlParams] {object}
   * @return {string}
   */
  aiApiUrl: function (path, objUrlParams) {
    let sUrlParams = '';
    if (!_.isEmpty(objUrlParams)) {
      if (/\?/.test(path)) {
        if (!/\?$/.test(path)) {
          sUrlParams += '&';
        }
      } else {
        sUrlParams += '?';
      }
      sUrlParams += $.param(objUrlParams);
    }

    return serverProfile.aiApiUrl + path + sUrlParams;
  },

  $: $,
  validate: validate,
  api: api,
  moment: moment,
  Recorder: window.Recorder,
  Marionette: Marionette,
  hbs: Handlebars,
  Vue: Vue,
  pako: pako,
  Vuex: Vuex,
  // external flash player, hosted on CDN

  TIME: new (function Time() {
    this.SECOND = 1e3;
    this.MINUTE = 60 * this.SECOND;
    this.HOUR = 60 * this.MINUTE;
    this.DAY = 24 * this.HOUR;
    this.WEEK = 7 * this.DAY;
    this.MONTH = 30 * this.DAY;
    this.YEAR = 365 * this.DAY;
    // https://en.wikipedia.org/wiki/Mental_chronometry
    this.MENTAL = {
      SOUND: 166, // reaction to sound
      VISUAL: 190, // reaction to visual
      λ: 256, // expectation trust
    };
    // Time in milliseconds of single frame under different Hz
    this.FRAME = {
      _30: 1 / 30,
      _60: 1 / 60,
    };
    // custom
    this.SPINNER_DELAY = this.MENTAL.λ;
    this.AUTOREFRESH_THRESHOLD = 2.5 * this.HOUR;
    // sec
    this.SOT_PLACEHOLDER_DURATION = 1e-6;
    // max allowed time gap between two assets or between asset and timeline beginning/end (seconds)
    this.MAX_INTERASSET_CLEARANCE = 1e-3 /* second*/;
    this.MAX_TIMELINE_TOTAL_GAP = 33e-3 /* second*/;
    this.MAX_STORY_DURATION = 720 /* second*/;
    this.MIN_FREE_TEXT_DURATION = 0.5 /* second */;
    this.MIN_FRAGMENT_DURATION = 0.6 /* second */;
    this.MIN_SCENE_DURATION = 3 /* second */;
    this.MIN_VIDEO_DURATION_FOR_BACKEND_SPRITE = 25 /* second */;
    this.MAX_OVERLAY_GAP_FOR_ANIMATION_CUT = 0.333333 /* second */;

    this.second = (sec = 1) => sec * this.SECOND;
    this.minute = (min = 1) => min * this.MINUTE;
    this.hour = (hour = 1) => hour * this.HOUR;
    this.day = (day = 1) => day * this.DAY;
    this.week = (week = 1) => week * this.WEEK;
    this.month = (month = 1) => month * this.MONTH;
    this.years = (year = 1) => year * this.YEAR;

    this.getTimeAgo = (ms) => {
      const texts = app.culture.time;
      const units = [
        { time: 0, text: texts.second, textPlural: texts.seconds },
        { time: this.MINUTE, text: texts.minute, textPlural: texts.minutes },
        { time: this.HOUR, text: texts.hour, textPlural: texts.hours },
        { time: this.DAY, text: texts.day, textPlural: texts.days },
        { time: this.WEEK, text: texts.week, textPlural: texts.weeks },
        { time: this.MONTH, text: texts.month, textPlural: texts.months },
        { time: this.YEAR, text: texts.year, textPlural: texts.years },
        { time: Infinity },
      ];

      const ago = ({ time, text, textPlural }) => {
        return function () {
          const value = Math.floor(ms / time);
          return value > 1 ? textPlural.replace('_', value) : text;
        };
      };

      let unit = units[0];
      let i = -1;

      while (++i < units.length) {
        if (ms >= units[i].time && ms < units[i + 1].time) {
          unit = units[i];
        }
      }

      // Without this, assets older then 360 days newer than 365 days will appear as 12 months
      if (unit.time === this.MONTH && Math.floor(ms / unit.time) === 12) {
        return unit.textPlural.replace('_', 11);
      }

      return ago(unit)();
    };
  })(),

  MAX_LIGHTBOX_VIDEO_DURATION: 90,
  MAX_ASSET_SCALE: 2,
  AI_LANGUAGE_LIST: [
    {
      id: 'BENGALI',
      label: 'Bengali',
      code: 'bn',
    },
    {
      id: 'KOREAN',
      label: 'Korean',
      code: 'ko',
    },
    {
      id: 'TURKISH',
      label: 'Turkish',
      code: 'tr',
    },
    {
      id: 'CHINESE_TRADITIONAL',
      label: 'Chinese (Traditional)',
      code: 'zh-Hant',
    },
    {
      id: 'PERSIAN',
      label: 'Persian',
      code: 'fa',
    },
    {
      id: 'URDU',
      label: 'Urdu',
      code: 'ur',
    },
    {
      id: 'THAI',
      label: 'Thai',
      code: 'th',
    },
    {
      id: 'HEBREW',
      label: 'Hebrew',
      code: 'he',
    },
    {
      id: 'GREEK',
      label: 'Greek',
      code: 'el',
    },
    {
      id: 'NORWEGIAN',
      label: 'Norwegian',
      code: 'no',
    },
    {
      id: 'FINNISH',
      label: 'Finnish',
      code: 'fi',
    },
    {
      id: 'HUNGARIAN',
      label: 'Hungarian',
      code: 'hu',
    },
    {
      id: 'ARABIC',
      label: 'Arabic',
      code: 'ar',
    },
    {
      id: 'CHINESE_SIMPLIFIED',
      label: 'Chinese (Simplified)',
      code: 'zh',
    },
    {
      id: 'CROATIAN',
      label: 'Croatian',
      code: 'hr',
    },
    {
      id: 'CZECH',
      label: 'Czech',
      code: 'cs',
    },
    {
      id: 'DANISH',
      label: 'Danish',
      code: 'da',
    },
    {
      id: 'DUTCH',
      label: 'Dutch',
      code: 'nl',
    },
    {
      id: 'ENGLISH',
      label: 'English',
      code: 'en',
    },
    {
      id: 'ENGLISH_UK',
      label: 'English (United Kingdom)',
      code: 'en',
    },
    {
      id: 'FRENCH',
      label: 'French',
      code: 'fr',
    },
    {
      id: 'GERMAN',
      label: 'German',
      code: 'de',
    },
    {
      id: 'ITALIAN',
      label: 'Italian',
      code: 'it',
    },
    {
      id: 'INDONESIAN',
      label: 'Indonesian',
      code: 'id',
    },
    {
      id: 'JAPANESE',
      label: 'Japanese',
      code: 'ja',
    },
    {
      id: 'LITHUANIAN',
      label: 'Lithuanian',
      code: 'lt',
    },
    {
      id: 'MALAY',
      label: 'Malay',
      code: 'ms',
    },
    {
      id: 'POLISH',
      label: 'Polish',
      code: 'pl',
    },
    {
      id: 'PORTUGUESE',
      label: 'Portuguese',
      code: 'pt',
    },
    {
      id: 'ROMANIAN',
      label: 'Romanian',
      code: 'ro',
    },
    {
      id: 'RUSSIAN',
      label: 'Russian',
      code: 'ru',
    },
    {
      id: 'SLOVAK',
      label: 'Slovak',
      code: 'sk',
    },
    {
      id: 'SLOVENIAN',
      label: 'Slovenian',
      code: 'sl',
    },
    {
      id: 'SPANISH',
      label: 'Spanish',
      code: 'es',
    },
    {
      id: 'SWEDISH',
      label: 'Swedish',
      code: 'sv',
    },
    {
      id: 'TAMIL',
      label: 'Tamil',
      code: 'ta',
    },
    {
      id: 'UKRAINIAN',
      label: 'Ukrainian',
      code: 'uk',
    },
    {
      id: 'VIETNAMESE',
      label: 'Vietnamese',
      code: 'vi',
    },
    {
      id: 'SERBIAN',
      label: 'Serbian',
      code: 'sr',
    },
    {
      id: 'AFRIKAANS',
      label: 'Afrikaans',
      code: 'af',
    },
    {
      id: 'ALBANIAN',
      label: 'Albanian',
      code: 'sq',
    },
    {
      id: 'AMHARIC',
      label: 'Amharic',
      code: 'am',
    },
    {
      id: 'ARMENIAN',
      label: 'Armenian',
      code: 'hy',
    },
    {
      id: 'AZERBAIJANI',
      label: 'Azerbaijani',
      code: 'az',
    },
    {
      id: 'BOSNIAN',
      label: 'Bosnian',
      code: 'bs',
    },
    {
      id: 'BULGARIAN',
      label: 'Bulgarian',
      code: 'bg',
    },
    {
      id: 'CATALAN',
      label: 'Catalan',
      code: 'ca',
    },
    {
      id: 'ESTONIAN',
      label: 'Estonian',
      code: 'et',
    },
    {
      id: 'FILIPINO',
      label: 'Filipino',
      code: 'tl',
    },
    {
      id: 'GEORGIAN',
      label: 'Georgian',
      code: 'ka',
    },
    {
      id: 'GUJARATI',
      label: 'Gujarati',
      code: 'gu',
    },
    {
      id: 'HAITIAN_CREOLE',
      label: 'Haitian Creole',
      code: 'ht',
    },
    {
      id: 'HAUSA',
      label: 'Hausa',
      code: 'ha',
    },
    {
      id: 'HINDI',
      label: 'Hindi',
      code: 'hi',
    },
    {
      id: 'ICELANDIC',
      label: 'Icelandic',
      code: 'is',
    },
    {
      id: 'IRISH',
      label: 'Irish',
      code: 'ga',
    },
    {
      id: 'KANNADA',
      label: 'Kannada',
      code: 'kn',
    },
    {
      id: 'KAZAKH',
      label: 'Kazakh',
      code: 'kk',
    },
    {
      id: 'LATVIAN',
      label: 'Latvian',
      code: 'lv',
    },
    {
      id: 'MACEDONIAN',
      label: 'Macedonian',
      code: 'mk',
    },
    {
      id: 'MALAYALAM',
      label: 'Malayalam',
      code: 'ml',
    },
    {
      id: 'MALTESE',
      label: 'Maltese',
      code: 'mt',
    },
    {
      id: 'MARATHI',
      label: 'Marathi',
      code: 'mr',
    },
    {
      id: 'MONGOLIAN',
      label: 'Mongolian',
      code: 'mn',
    },
    {
      id: 'PASHTO',
      label: 'Pashto',
      code: 'ps',
    },
    {
      id: 'PUNJABI',
      label: 'Punjabi',
      code: 'pa',
    },
    {
      id: 'SINHALA',
      label: 'Sinhala',
      code: 'si',
    },
    {
      id: 'SOMALI',
      label: 'Somali',
      code: 'so',
    },
    {
      id: 'SWAHILI',
      label: 'Swahili',
      code: 'sw',
    },
    {
      id: 'TELUGU',
      label: 'Telugu',
      code: 'te',
    },
    {
      id: 'UZBEK',
      label: 'Uzbek',
      code: 'uz',
    },
    {
      id: 'WELSH',
      label: 'Welsh',
      code: 'cy',
    },
  ],

  ASPECT_RATIOS: {
    ALL: {
      friendlyName: 'All aspect ratios',
      friendlyVal: 'All',
    },
    ASPECT_16_9: {
      name: 'ASPECT_16_9',
      friendlyVal: '16x9',
      friendlyName: 'Wide',
      ratio: 16 / 9,
      width: 16,
      height: 9,
    },
    ASPECT_4_5: {
      name: 'ASPECT_4_5',
      friendlyVal: '4x5',
      friendlyName: 'Portrait',
      ratio: 4 / 5,
      width: 4,
      height: 5,
    },
    ASPECT_1_1: {
      name: 'ASPECT_1_1',
      friendlyVal: '1x1',
      friendlyName: 'Square',
      ratio: 1,
      width: 1,
      height: 1,
    },
    ASPECT_9_16: {
      name: 'ASPECT_9_16',
      friendlyVal: '9x16',
      friendlyName: 'Vertical',
      ratio: 9 / 16,
      width: 9,
      height: 16,
    },
    ASPECT_3_4: {
      name: 'ASPECT_3_4',
      friendlyVal: '3x4',
      friendlyName: 'Instagram',
      ratio: 3 / 4,
      width: 3,
      height: 4,
    },
    ASPECT_3_2: {
      name: 'ASPECT_3_2',
      friendlyVal: '3x2',
      friendlyName: 'Photo',
      ratio: 3 / 2,
      width: 3,
      height: 2,
    },
  },

  TRANSCRIPT_OPENER: {
    TOOLBAR: 'Toolbar Button',
    LIGHTBOX_MENU: 'Lightbox thumbnail',
    LIGHTBOX_PREVIEW: 'Lightbox Preview',
    TIMELINE_VO: 'Timeline VO menu/dc',
    TIMELINE_TEXT: 'Timeline text doubleclick',
  },

  config: {
    chromeExtension: {
      name: 'wochits-article2video',
      id: 'gfopoepjefibcmaehoagiclnnpmijedi',
    },
  },

  // characters
  TEXT_LIMIT_COMMENT: 500,
  TEXT_LIMIT_REJECTION: 255,

  FACEBOOK_ID: 952363431465016,

  CONDITIONAL_REALTIME_IGNORE_FIELDS: ['scale', 'offset'],
  REALTIME_IGNORE_FIELDS: ['wiperType', 'transition'],

  RealtimePreviewType: {
    VIDEO: 'VIDEO',
    // preview streaming
    VIDEO_PARTS: 'VIDEO_PARTS',
    STATIC: 'STATIC',
    STATIC_MASK: 'STATIC_MASK',
  },
  taskManagerFilters: null,
  taskManagerSort: [{ field: 'relevance', order: 'asc' }],
  DASHBOARD_RESULTS_PER_PAGE: 60,
  DASHBOARD_HOME_MAX_RESULTS_PER_ROW: 7,

  MAX_TITLE_LENGTH: 256, // used for title validation in VPOD and metadata editor

  ASSET_EDITOR_WIDTH: 688,
  ASSET_EDITOR_HEIGHT: 387,
  ASSET_EDITOR_RATIO: () => app.ASSET_EDITOR_WIDTH / app.ASSET_EDITOR_HEIGHT,
  CURTAINS_GAP: () =>
    100 *
    ((1 -
      app.ASPECT_RATIOS[app.currentAspectRatio].ratio /
        app.ASSET_EDITOR_RATIO()) /
      2),
  ZERO_WIDTH_SPACE: '\u200B',
  TEXT_OVERLAY_VERSION: '4',

  TEXT_STYLES: {
    PRIVATE: 'USER_PRIVATE',
    SHARED: 'SHARED_BETWEEN_ORG',
  },

  PROVIDERS: {
    WOCHIT: {
      id: 'WOCHIT',
      RESULTS_PER_PAGE: 60,
      BUFFER_SIZE: 140,
      url: () => '/api/v1/assets',
      match: (asset) =>
        app.assetType.isIMAGE(asset) || app.assetType.isVIDEO(asset),
    },
    WOCHIT_CREATIVE: {
      id: 'WOCHIT_CREATIVE',
      RESULTS_PER_PAGE: 20,
      REFILL_COUNT: 100,
      url: () => '/api/v1/assets/dynamic',
      match: (asset) =>
        app.assetType.isIMAGE(asset) || app.assetType.isVIDEO(asset),
    },
    TWITTER: {
      id: 'TWITTER',
      RESULTS_PER_PAGE: 40,
      BUFFER_SIZE: 200,
      url: () => '/api/v1/assets/search/social/twitter',
      match: (asset) => app.assetType.isTWITTER(asset),
    },
    INSTAGRAM: {
      id: 'INSTAGRAM',
      RESULTS_PER_PAGE: 40,
      BUFFER_SIZE: 50,
      url: (type) => {
        if (type === 'USERNAME') {
          return '/api/v1/assets/search/social/instagram/user_posts';
        }
        return '/api/v1/assets/search/social/instagram';
      },
      match: (asset) => app.assetType.isINSTAGRAM(asset),
    },
    GIF: {
      id: 'GIF',
      RESULTS_PER_PAGE: 40,
      BUFFER_SIZE: 48,
      url: () => '/api/v1/assets/search/gif',
      match: (asset) => app.assetType.isGIF(asset),
    },
    MY_GALLERY: {
      id: 'MY_GALLERY',
      RESULTS_PER_PAGE: 40,
      BUFFER_SIZE: 50,
      url: (format) => {
        return '/api/v1/assets/';
      },
    },
    FAVORITES: {
      id: 'FAVORITES',
      RESULTS_PER_PAGE: 40,
      BUFFER_SIZE: 500,
      url: () => '/api/v1/assets/favorite',
      match: (asset) =>
        app.assetType.isIMAGE(asset) || app.assetType.isVIDEO(asset),
    },
    KALTURA: {
      id: 'KALTURA',
      RESULTS_PER_PAGE: 35,
      BUFFER_SIZE: 35 /* Warning: Kaltura accept max of 70 flavors per requests, don't increase 35 */,
      url: () => '/api/v1/assets/kaltura',
      match: (asset) =>
        app.assetType.isIMAGE(asset) || app.assetType.isVIDEO(asset),
    },
    WOCHIT_OWNED_ASSETS: {
      id: 'WOCHIT_OWNED_ASSETS',
      RESULTS_PER_PAGE: 40,
      BUFFER_SIZE: 500,
      url: () => '/api/v1/assets/wochitOwned/',
      match: (asset) =>
        app.assetType.isIMAGE(asset) || app.assetType.isVIDEO(asset),
    },
  },
  PREVIEW_SOURCE: {
    ASSET_GALLERY: {
      id: 'ASSET_GALLERY',
    },
  },

  DEFAULT: {
    CHANNEL_ID: 1,
    ASPECT_RATIO: 'ASPECT_16_9',
    EXCLUSIVE_AUDIO_MIMES:
      'audio/mpeg,audio/mp3,audio/wav,audio/x-m4a,audio/webm',
    SUPPORTED_AUDIO_EXTENSIONS: ['WAV', 'MP3', 'M4A', 'WEBM'],
    BROADLY_INCLUSIVE_MEDIA_MIMES:
      'video/*,image/*,application/mxf,audio/*' /** sec */,
    preNarrationTime: 1.4,
    postNarrationTime: 1.4,
    /** limits video fragment startTime and endTime */
    videoDurationPadding: 0.0,
    videoPoster: '/resources/img/video1.png',
    storyThumbnailImage:
      'https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/video-poster-placeholder.png',
    userAvatarImage: '/resources/img/user-placeholder.png',
    userName: 'Wochit User',
    novoExampleVideo:
      '//dp4stoslb9jdl.cloudfront.net/artifacts/headlines/singlePlus/99157201/99157201-1280x720_Jan_07_2016_13_23_29.MP4',
    voExampleVideo:
      '//dp4stoslb9jdl.cloudfront.net/artifacts/headlines/singlePlus/99222758/99222758-1280x720_Jan_07_2016_13_22_35.MP4',
    customOverlaysIcons:
      '//dp4stoslb9jdl.cloudfront.net/Resources/studio/co-icons/{0}',
    widgetThumbnails:
      '//dp4stoslb9jdl.cloudfront.net/Resources/studio/co-widget-thumbnails/{0}',
    producingVideo:
      '//dp4stoslb9jdl.cloudfront.net/Resources/studio/video/producing_video.webm',
    fullPreviewGeneratingVideo:
      '//dp4stoslb9jdl.cloudfront.net/Resources/studio/video/small_machine_2020.gif',
    MISSING_ASSET_IMG:
      'https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/instant-player/missing-asset.png',
    MISSING_ASSET_IMG_NARROW:
      'https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/instant-player/missing-asset.png',
    EMPTY_TIMELINE_IMG:
      'https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/instant-player/empty-timeline.png',
    EMPTY_TIMELINE_IMG_NARROW:
      'https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/instant-player/empty-timeline-narrow.png',
    EMPTY_STORY_THUMBNAIL:
      'https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/empty_thumb_pattern.png',
    ROW_DURATION: 30,
  },

  ANIMATION: {
    NONE: {
      id: 'free_text_Static',
      continueId: 'continue',
      displayText: 'None',
      icon: null,
    },
    CONTINUE: {
      id: 'continue',
      displayText: 'Auto',
      icon: '/resources/img/toAnimations/auto-animation-v1.png',
    },
    FALLBACK: {
      icon: '/resources/img/toAnimations/fallback-animation-v1.png',
    },
  },

  CONNECTION: {
    ESTABLISHED: 'CONNECTION_ESTABLISHED',
    LOST_OFFLINE: 'CONNECTION_LOST_OFFLINE',
    LOST_RECONNECT: 'CONNECTION_LOST_RECONNECT',
    LOST_SUPPORT: 'CONNECTION_LOST_SUPPORT',
    REAUTHENTICATE: 'CONNECTION_REAUTHENTICATE',
    USER_WAS_KICKED: 'USER_WAS_KICKED',
  },

  TWITTER_PLACEHOLDER: (function () {
    const URL = '/resources/img/twitterAsset.png';
    const URL_BIG = '/resources/img/twitterAssetBig.png';
    const URL_NARROW = '/resources/img/twitterAssetNarrow.jpeg';

    return {
      URL: URL,
      URL_BIG: URL_BIG,
      URL_NARROW: URL_NARROW,
      WIDTH: 151,
      HEIGHT: 84,
      cache: function () {
        const img = new Image();
        img.src = URL;
        const imgBig = new Image();
        imgBig.src = URL_BIG;
        const imgNarrow = new Image();
        imgNarrow.src = URL_NARROW;
      },
    };
  })(),

  FACEBOOK_PLACEHOLDER: (function () {
    const URL = '/resources/img/facebookAsset.jpeg';
    const URL_BIG = '/resources/img/facebookAssetBig.jpeg';
    const URL_NARROW = '/resources/img/facebookAssetNarrow.jpeg';

    return {
      URL: URL,
      URL_BIG: URL_BIG,
      URL_NARROW: URL_NARROW,
      cache: function () {
        const img = new Image();
        img.src = URL;
        const imgBig = new Image();
        imgBig.src = URL_BIG;
        const imgNarrow = new Image();
        imgNarrow.src = URL_NARROW;
      },
    };
  })(),

  INSTAGRAM_PLACEHOLDER: (function () {
    const URL = '/resources/img/instagramAsset.png';

    return {
      URL: URL,
      cache: function () {
        const img = new Image();
        img.src = URL;
      },
    };
  })(),

  // @deprecated in fb-new-vo
  ProducingStates: [
    'AFTER_ARTICLE_MANUAL_LATE_CLONING',
    'AFTER_ARTICLE_MANUAL_WORK',
    'AFTER_MANUAL_HEADLINES_PREPARE',
    'AFTER_HEADLINES_PREPARE',
    'AFTER_GOOSE',
    'VIDEO_GEN_IN_PROGRESS',
    'AFTER_VIDEO_GENERATION',
    'AFTER_VIDEO_FLOW_INSERT',
    'HEADLINES_POST_PROCESSING',
    'AFTER_TRIGGERING_QUEUE_INSERT',
  ],
  // @deprecated in fb-new-vo
  ErrorProducingStates: [
    'ERROR_ARTICLE_PREPARE_MANUAL',
    'ERROR_GOOSE',
    'ERROR_VIDEO_GENERATION',
    'ERROR_HEADLINES_POST_PROCESSING',
  ],
  // @deprecated in fb-new-vo
  FinishProducingStates: ['AFTER_PUBLISH'],
  RECORD_SEGMENT_TIMER_URL:
    '//dp4stoslb9jdl.cloudfront.net/Resources/studio/video/recording_timer_1.mp4',
  KEYBOARD_LAYER: {
    GLOBAL: 'global',
    TIBTZ: 'timebase timeline zoom',
    RCP: 'rough cut player',
    IAN: 'in asset navigation',
    TO: 'text overlay',
    CAD: 'close asset dialog',
    BTP: 'back to preview',
    CTO: 'close twitter operators',
    DMV: 'dashboard my videos',
    IAC: 'instagram auto complete',
    UNDO: 'undo redo',
    FPV: 'full preview view',
    DUMMY: 'dummy',
    SEARCH_MEDIA: 'search media',
    TLLB: 'Timeline lightbox',
    PRODUCED: 'produced video popup',
    MFM: 'media filtering modal',
    FUV: 'file upload view',
    MDE: 'metadata editor',
    SM: 'settings modal',
    AE: 'asset editor',
    LFM: 'linked fields menu',
    TIMELINE: 'timeline adapter',
    FRAGMENT_EDITOR: 'fragment editor',
    MPP: 'mass produce preview',
    TRANSCRIBE: 'transcription modal',
  },

  /**
   * TaskManager tab
   */
  TASK_LIST_TYPE: {
    STORIES: 'stories',
    VIDEOS: 'videos',
  },

  privacyType: {
    ORGANIZATION_PRIVATE: 'ORGANIZATION_PRIVATE',
    USER_PRIVATE: 'USER_PRIVATE',
    PUBLIC: 'PUBLIC',
  },

  attributionType: {
    NONE: 'NONE',
    SHOW_IN_VIDEO: 'SHOW_IN_VIDEO',
  },

  uploadDestination: {
    ASSET_GALLERY: 'ASSET_GALLERY',
    LIGHTBOX: 'LIGHTBOX',
  },

  analytics: function (event, options) {
    Analytics.trigger(event, options);
  },

  FP_ROUND: 1e6,
  /**
   * Safe floating point
   * @param fp
   * @param [precision] {number}
   * @return {number},
   */
  sfp: function (fp, precision) {
    precision = _.isNumber(precision) ? precision : this.FP_ROUND;
    return Math.round(parseFloat(fp) * precision) / precision;
  },

  /**
   * show/hide full screen blocking spinner
   * that mimics _.debounce and differs in such a way that triggering one function cancels the opposite.
   */
  delayedSpinner: new (function () {
    const $spinner = $('#spinner').modal({
      backdrop: 'static',
      keyboard: false,
      show: false,
    });
    const timer = {
      show: null,
      hide: null,
    };

    return {
      show: function (options) {
        options = $.extend(
          {
            delay: app.TIME.SPINNER_DELAY,
            msg: '',
            msg2: '',
          },
          options
        );

        const $msg = $spinner.find('.js-spinner-msg');
        const $msgTwo = $spinner.find('.js-spinner-msg-two');

        $msg.html(options.msg);
        $msgTwo.html(options.msg2);

        timer.hide = window.clearTimeout(timer.hide);

        if (!timer.show) {
          timer.show = window.setTimeout(function () {
            timer.show = null;
            $spinner.show();
          }, options.delay);
        }
      },

      isVisible: function () {
        return !!timer.show || $spinner.is(':visible');
      },

      hide: function (options) {
        options = $.extend(
          {
            delay: app.TIME.SPINNER_DELAY,
          },
          options
        );

        timer.show = window.clearTimeout(timer.show);

        if (!timer.hide) {
          timer.hide = window.setTimeout(function () {
            timer.hide = null;
            $spinner.hide();
          }, options.delay);
        }
      },
    };
  })(),

  css(str) {
    const style = document.createElement('STYLE');
    style.appendChild(document.createTextNode(str.normalizeSpace()));
    document.head.appendChild(style);
  },

  cursorWorking: new (function () {
    const $app = $('#wtApp');

    return {
      show: function () {
        $app.addClass('cursorProgress');
      },
      hide: function () {
        $app.removeClass('cursorProgress');
      },
    };
  })(),

  VIDEO_PLAY_TIME_CSS_CLASS: {
    START_ID: 'asset-||ASSET_ID||-display-time-start',
    END: 'video-play-time-end',
    PLAY_TIME: 'video-play-time',
    PLAY_TIME_BLOCK: 'video-play-time-block',
    PLAY_BLOCK: 'play-block',
    getStartID: function (assetId) {
      return this.START_ID.replace('||ASSET_ID||', assetId);
    },
  },

  /** datamodel.enums.ImageEffectType*/
  imageEffectType: {
    ZOOM_IN: 'ZOOM_IN',
    ZOOM_OUT: 'ZOOM_OUT',
    CROP: 'CROP',
    NONE: 'NONE',
    _effectTypeFrom: function (o) {
      if (_.isObject(o) && _.isFunction(o.getAGD)) {
        return o.getAGD('effectType');
      } else if (_.isObject(o) && _.isObject(o.agd)) {
        return o.agd.effectType;
      } else if (_.isString(o)) {
        return o;
      } else {
        console.trace('Should not happen');
      }
    },
    isDefined: function (o) {
      const effectType = this._effectTypeFrom(o);
      return (
        effectType === this.ZOOM_IN ||
        effectType === this.ZOOM_OUT ||
        effectType == this.CROP
      );
    },
    isZOOM: function (o) {
      return (
        this._effectTypeFrom(o) === this.ZOOM_IN ||
        this._effectTypeFrom(o) === this.ZOOM_OUT
      );
    },
    isCROP: function (o) {
      return this._effectTypeFrom(o) === this.CROP;
    },
  },

  // Plan.java
  planType: {
    FREE: 'FREE',
    PAID: 'PAID',
    _getFrom: function (obj, prop) {
      if (
        typeof obj === 'object' &&
        'get' in obj &&
        typeof obj.get === 'function'
      ) {
        return obj.get(prop);
      } else if (typeof obj === 'object' && prop in obj) {
        return obj[prop];
      } else {
        return obj;
      }
    },
    isFREE: function (model) {
      return this._getFrom(model, 'plan') === this.FREE;
    },
    isPAID: function (model) {
      return this._getFrom(model, 'plan') === this.PAID;
    },
  },

  storyStage: {
    NEW: 'NEW',
    PREVIEW: 'PREVIEW',
    TASK_DATA: 'TASK_DATA',
  },

  assetsSource: {
    NORMAL: 'NORMAL',
  },

  // FileUploadStatus.java
  uploadStatus: {
    UPLOAD: 'UPLOAD',
    PROCESSING: 'PROCESSING',
    DONE: 'DONE',
    FAILED: 'FAILED',
    DELETED: 'DELETED',
  },
  uploadType: {
    VISUAL_ASSET: 'VISUAL_ASSET',
    AUDIO_ASSET: 'AUDIO_ASSET',
    NARRATION_ASSET: 'NARRATION_ASSET',
  },
  mediaAssetSubType: {
    BG_MUSIC: 'BG_MUSIC',
    NARRATION: 'NARRATION',
  },

  transcribeSubType: {
    NARRATION: 'NARRATION',
    VIDEO: 'VIDEO',
  },

  /**
   * YoutubeUploadStatus
   */
  youtubeUploadStatus: {
    MISSING_ARGS: 'MISSING_ARGS',
    ABSENT: 'ABSENT',
    FAIL: 'FAIL',
    INIT_UPLOAD: 'INIT_UPLOAD',
    UPLOAD_IN_PROGRESS: 'UPLOAD_IN_PROGRESS',
    PROCESS_IN_PROGRESS: 'PROCESS_IN_PROGRESS',
    DONE: 'DONE',
    _getFrom: function (obj, prop) {
      if (
        typeof obj === 'object' &&
        'get' in obj &&
        typeof obj.get === 'function'
      ) {
        return obj.get(prop);
      } else if (typeof obj === 'object' && prop in obj) {
        return obj[prop];
      } else {
        return obj;
      }
    },
    isDONE: function (model) {
      return this._getFrom(model, 'youtubeUploadStatus') === this.DONE;
    },
  },

  fragmentPreview: {
    CREATING: 'Generating Preview',
    REQUEST: 'Requesting Preview',
    BUFFERING: 'Buffering Video',
    WAIT: 'Processing',
    FAIL: 'Preview Failed!',
  },

  storyStartWith: {
    SEARCH: 'search',
    MY_GALLERY: 'mygallery',
  },

  storyStartPoint: {
    // only start page events !!
    TRENDING: 'TRENDING', // from trending videos
    MEDIA_SELECTION: 'MEDIA_SELECTION', // from media selection,
    MEDIA_SELECTION_NOVO: 'MEDIA_SELECTION_NOVO', // from timeline / novo story
    URL: 'URL', // from start page with url
    TEXT: 'TEXT', // from start page from text
    INSTANT: 'INSTANT', // start from exiting
    MY_GALLERY: 'MY_GALLERY', // start from my gallery
    BACK_TO: 'BACK_TO',
    STORY_TEMPLATE: 'STORY_TEMPLATE',
    FEED: 'FEED',
    DUPLICATE_IN_PROGRESS_STORY: 'DUPLICATE_IN_PROGRESS_STORY',
    STORYBOARD: 'STORYBOARD',
  },

  gradientTypes: {
    LINEAR: 'LINEAR',
    RADIAL: 'RADIAL',
  },

  regexs: {
    areaWithZIndex: new RegExp(/^(\d+(\.\d+)? ){4}\d+$/),
  },

  rcpShouldCrop: function (model) {
    const agd = this.assetType._getFrom(model, 'agd');

    return (
      (agd.scale || agd.offset) &&
      !this.assetType.isContainOverlay(model) &&
      (this.assetType.isFRAGMENT(model) || this.assetType.isIMAGE(model)) &&
      !this.assetType.isSOCIALFRAGMENT(model) &&
      !app.realTimePreviewController.agdRequiresRtp(model)
    );
  },

  assetType: {
    UNDEFINED: 'metaasset',
    IMAGE: 'imagemetaasset',
    WALLPAPER: 'wallpapermetaasset',
    VIDEO: 'videometaasset',
    FRAGMENT: 'fragmentmetaasset',
    WEBPAGE: 'webpagemetaasset',
    MAP: 'mapmetaasset',
    AUDIO: 'audiometaasset',
    SOCIAL: 'socialmetaasset',
    SOCIALFRAGMENT: 'socialfragmentmetaasset',
    GIF: 'gifmetaasset',
    _allKinds: [],

    _getFrom: function (obj, prop) {
      if (
        typeof obj === 'object' &&
        'get' in obj &&
        typeof obj.get === 'function'
      ) {
        return obj.get(prop);
      } else if (typeof obj === 'object' && prop in obj) {
        return obj[prop];
      } else if (typeof obj === 'object' && typeof obj.metadata === 'object') {
        return obj.metadata[prop];
      } else {
        console.trace('None supported arguments.');
        return null;
      }
    },
    isExpired: function (model) {
      const expiration = this._getFrom(model, 'metadata').expirationDate;
      return !!expiration && Date.now() >= expiration;
    },
    isIMAGE: function (model) {
      const type = this._getFrom(model, 'type');
      return type === this.IMAGE || type === this.MAP;
    },
    isWALLPAPER: function (model) {
      const type = this._getFrom(model, 'type');
      return type === this.WALLPAPER;
    },
    isOnlyIMAGE: function (model) {
      const type = this._getFrom(model, 'type');
      return type === this.IMAGE;
    },
    isMAP: function (model) {
      const type = this._getFrom(model, 'type');
      return (
        type === this.MAP ||
        (type === this.IMAGE &&
          /^map/i.test(this._getFrom(model, 'providerId')))
      );
    },
    isAUDIO: function (model) {
      const assetType = this._getFrom(model, 'type');
      const assetMetadataType = this._getFrom(model, 'assetType');
      return assetType === this.AUDIO || assetMetadataType === 'AUDIO';
    },
    isWEBPAGE: function (model) {
      return this._getFrom(model, 'type') === this.WEBPAGE;
    },
    isVIDEO: function (model) {
      return this._getFrom(model, 'type') === this.VIDEO;
    },
    isGIF: function (model) {
      return this._getFrom(model, 'type') === this.GIF;
    },
    isFRAGMENT: function (model) {
      return (
        this._getFrom(model, 'type') === this.FRAGMENT ||
        this._getFrom(model, 'type') === this.SOCIALFRAGMENT
      );
    },
    isSOCIALFRAGMENT: function (model) {
      return this._getFrom(model, 'type') === this.SOCIALFRAGMENT;
    },
    isSOCIAL: function (model) {
      return this._getFrom(model, 'type') === this.SOCIAL;
    },
    isSOCIALIMAGE: function (model) {
      return (
        this._getFrom(model, 'type') === this.SOCIAL &&
        this._getFrom(model, 'metadata').providerLowResAssetUrl
      );
    },
    isSOCIALVIDEO: function (model) {
      return (
        this.isSOCIAL(model) &&
        ((this.isFACEBOOK(model) &&
          /video/i.test(
            this._getFrom(model, 'metadata').facebookAssetMetadata
              .facebookPostType
          )) ||
          (this.isINSTAGRAM(model) &&
            /video/i.test(
              this._getFrom(model, 'metadata').instagramAssetMetadata
                .instagramPostType
            )) ||
          (this.isTWITTER(model) &&
            /video/i.test(this._getFrom(model, 'metadata').twitterPostType)))
      );
    },
    isTWITTER: function (model) {
      return (
        (this.isSOCIAL(model) || this.isSOCIALFRAGMENT(model)) &&
        /^TWITTER/i.test(this._getFrom(model, 'metadata').providerId)
      );
    },
    isFACEBOOK: function (model) {
      return (
        (this.isSOCIAL(model) || this.isSOCIALFRAGMENT(model)) &&
        /^FACEBOOK/i.test(this._getFrom(model, 'metadata').providerId)
      );
    },
    isINSTAGRAM: function (model) {
      return (
        (this.isSOCIAL(model) || this.isSOCIALFRAGMENT(model)) &&
        /^INSTAGRAM/i.test(this._getFrom(model, 'metadata').providerId)
      );
    },
    isINFOGRX: function (model) {
      return this.isWEBPAGE(model) || this.isMAP(model);
    },
    /**
     * Backend decides if its copyrighted asset or not.
     * Previous logic was: (!this.isMyGalleryBucket() && !this.isInfographicsBucket())
     * @return {boolean}
     */
    isCopyRighted: function (model) {
      model = model.attributes ? model.attributes : model;
      return model.metadata.licensed;
    },
    isEffects: function (model) {
      let result = false;
      // /console.log('agd', model.agd);
      if (model.agd) {
        Object.keys(model.agd).forEach(function (key) {
          if (
            model.agd[key] &&
            !_.isEmpty(model.agd[key]) &&
            app.REALTIME_IGNORE_FIELDS.indexOf(key) === -1
          ) {
            result = true;
          }
        });
      }

      return result;
    },
    isFairUse: function (model) {
      model = model.attributes ? model.attributes : model;
      return (
        !model.metadata.licensed &&
        model.metadata.providerId !== 'asset_server' &&
        !app.assetType.isWEBPAGE(model) &&
        !app.assetType.isMAP(model)
      );
    },
    isEditorial: function (model) {
      model = model.attributes ? model.attributes : model;
      const providerId = _.isObject(model.metadata)
        ? model.metadata.providerId
        : null;
      return _.includes(tk.context._editorialProviders, providerId);
    },
    isCommercial: function (model) {
      model = model.attributes ? model.attributes : model;
      const providerId = _.isObject(model.metadata)
        ? model.metadata.providerId
        : null;
      return _.includes(tk.context._commercialProviders, providerId);
    },
    isInMyGallery: function (model) {
      model = model.attributes ? model.attributes : model;
      return 'asset_server' === model.metadata.providerId;
    },
    isAssetFromEditableProvider: function (mAsset) {
      const providerId = this._getFrom(mAsset, 'metadata').providerId;
      const editableProviders = tk.context._editableProviders;

      return editableProviders.indexOf(providerId) > -1;
    },

    isDynamicProvider: function (mAsset) {
      const providerId = this._getFrom(mAsset, 'metadata').providerId;
      const dynamicProviders = [
        'kaltura',
        'Getty_images_dynamic',
        'Getty_videos_dynamic',
        'ClipStock_videos_dynamic',
        'Shutterstock_images_dynamic',
        'Shutterstock_videos_dynamic',
      ];
      return dynamicProviders.includes(providerId);
    },

    isContainOverlay: function (mAsset) {
      return (mAsset.overlayTracks || []).length > 0;
    },

    /**
     * Get string that tests given asset for all kinds
     * @return {string}
     * @example "isGIF,isCopyRighted"
     */
    is: function (asset) {
      if (!this._allKinds.length) {
        for (const kind in this) {
          if (
            Object.getOwnPropertyDescriptor(this, kind) &&
            typeof this[kind] === 'function' &&
            /^is.+/.test(kind)
          ) {
            this._allKinds.push(kind);
          }
        }
      }
      const rv = [];
      for (let n = 0, N = this._allKinds.length; n < N; n++) {
        if (this[this._allKinds[n]](asset)) {
          rv.push(this._allKinds[n]);
        }
      }
      return rv.join();
    },
    isFavorite(asset) {
      return (
        asset &&
        asset.metadata &&
        asset.metadata.favoriteOwner &&
        !!asset.metadata.favoriteOwner
      );
    },
  },

  getAssetAnalyticsData: function (asset, story) {
    asset = asset.attributes || asset;
    story = story.attributes || story;
    return {
      'Asset Source': asset.metadata.providerId,
      'Asset Type': asset.type.replace('metaasset', '').toUpperCase(),
    };
  },

  assetScore: {
    EXCELLENT: 'EXCELLENT',
    _from: function (obj) {
      if (
        typeof obj === 'object' &&
        'get' in obj &&
        typeof obj.get === 'function'
      ) {
        return obj.get('score');
      } else if (
        typeof obj === 'object' &&
        'score' in obj &&
        typeof obj.score === 'string'
      ) {
        return obj.score;
      } else if (typeof obj === 'string') {
        return obj;
      } else {
        console.trace('Non supported argument.');
        return null;
      }
    },
    isSELECTED: function (model) {
      return this._from(model) === this.EXCELLENT;
    },
  },

  generateAssetId: function (asset) {
    let result = null;
    if (app.assetType.isFRAGMENT(asset)) {
      result = 'V-SSS-EEE-XXX'
        .replace(/V/, '2')
        .replace(/SSS/, _.isDefined(asset.startTime) ? asset.startTime : 'SSS')
        .replace(/EEE/, _.isDefined(asset.endTime) ? asset.endTime : 'EEE')
        .replace(
          /XXX/,
          asset.metadata && asset.metadata.assetUUID
            ? asset.metadata.assetUUID
            : 'XXX'
        );
    } else if (
      !_.isEmpty(asset.metadata) &&
      !_.isEmpty(asset.metadata.assetUUID)
    ) {
      result = asset.metadata.assetUUID;
    }
    return result;
  },

  assetUniqueIdentifier: function (asset) {
    asset = asset.attributes || asset;
    let identifier = asset.metadata.assetUUID;
    if (asset.metadata.providerAssetId && asset.metadata.providerId) {
      identifier = `dynamic_${asset.metadata.providerId}_${asset.metadata.providerAssetId}`;
    }
    return identifier;
  },
  getOnedriveClientId: function () {
    return this.rootStore.getters['user/box'].isProd()
      ? 'f018cdc0-3aa5-43d4-baaf-95a5c4474840'
      : 'a83c02ab-0bf3-4ebd-8190-6e5ed2c5cc0b';
  },
  generatePublishUrl: function (id) {
    const url = this.rootStore.getters['user/box'].isProd()
      ? 'https://share.wochit.com/'
      : 'https://share-test.wochit.com/';
    return url + id;
  },
  generatePricingUrl: function () {
    const params = _.pick(app.rootStore.state.user, [
      'firstName',
      'lastName',
      'username',
    ]);
    const url =
      (this.rootStore.getters['user/box'].isProd()
        ? 'https://www.wochit.com/vip-package/?'
        : 'https://wp-dev.wochit.com/vip-package/?') + $.param(params);
    return url;
  },
  clearErrorTooltips: function () {
    $('.tooltip, .popover').remove();
  },
  getNotificationsPermission: function () {
    if (
      !!window.Notification &&
      !!window.Notification.requestPermission &&
      window.Notification.permission !== 'granted'
    ) {
      window.Notification.requestPermission();
    } else if (
      !!window.webkitNotifications &&
      window.webkitNotifications.checkPermission() !== 0
    ) {
      // 0 is PERMISSION_ALLOWED
      window.webkitNotifications.requestPermission();
    }
  },

  checkNotificationPermission: function () {
    let ret = false;
    if (window.webkitNotifications) {
      ret = window.webkitNotifications.checkPermission() === 0;
    } else if (window.Notification) {
      ret = window.Notification.permission === 'granted';
    }
    return ret;
  },

  normalizeEx: function (str, options) {
    let rv = str || '';

    options = $.extend(
      true,
      {
        replaceCustom: null, // [{that: /regexp/gm, withThat: 'string' }, ...]
        trimEdges: true,
        normalizeSpaces: true,
        normalizeNewLines: true,
        replaceNLbySpace: true,
      },
      options || {}
    );

    if (_.isArray(options.replaceCustom) && !_.isEmpty(options.replaceCustom)) {
      _.each(options.replaceCustom, function (custom) {
        rv = rv.replace(custom.that, custom.withThat);
      });
    }

    // replace no-break-space (ASCII: 160) with regular space
    rv = rv.replace(/\u00A0/gm, ' ');

    if (options.normalizeSpaces) {
      rv = rv.replace(/[ \t]+/gm, ' ');
    }

    if (options.normalizeNewLines) {
      rv = rv.replace(/[\r\n]+/gm, '\n');
      rv = rv.replace(/\n+/gm, '\n');
    }

    if (options.replaceNLbySpace) {
      rv = rv.replace(/\n+/gm, ' ');
    }

    if (options.trimEdges) {
      rv = rv.replace(/^\s+|\s+$/gm, '');
    }

    return rv;
  },

  // eslint-disable-next-line no-useless-escape
  rgEscapeRegExpOperators: /([\^\$\(\)\{\}\[\]\.\?\-\\\/\*\|\+])/gm,
  escapeRegExpOperators: function (str) {
    return str.replace(this.rgEscapeRegExpOperators, '\\$1');
  },
  string2RegExp: function (term, mode) {
    if (!_.isString(mode)) {
      mode = 'mig';
    }
    return new RegExp(this.escapeRegExpOperators(term), mode);
  },

  seconds2Minutes: function (seconds) {
    const time = {
      seconds: Math.floor(seconds % 60),
      minutes: Math.floor(seconds / 60),
      number2LeadingZeroString: function (i) {
        return String(i < 10 ? '0' + i : i);
      },
      toString: function () {
        return (
          this.number2LeadingZeroString(this.minutes) +
          ':' +
          this.number2LeadingZeroString(this.seconds)
        );
      },
    };
    return time.toString();
  },

  seconds2MinutesDisplay: function (seconds, tenths) {
    const time = {
      seconds: Math.floor(seconds % 60),
      minutes: Math.floor(seconds / 60),
      tenths: Math.floor((seconds - Math.floor(seconds)) * 10),
      number2LeadingZeroString: function (i, minutes) {
        if (minutes && i === 0) {
          return '';
        } else if (minutes && i < 10) {
          return i;
        }
        return String(i < 10 ? '0' + i : i);
      },
      toString: function () {
        return (
          this.number2LeadingZeroString(this.minutes, true) +
          ':' +
          this.number2LeadingZeroString(this.seconds) +
          (tenths ? '.' + this.tenths : '')
        );
      },
    };
    return time.toString();
  },

  /**
   * Allow lazy AudioContext usage
   * There are maximum of 6 instances per domain
   * Get will provide new or same or null if failed during creation
   * Close will free use of current AC to other pages so be sure to call it as soon as possible
   */
  audioContext: new (function () {
    const AudioContext =
      window.AudioContext ||
      window.webkitAudioContext ||
      window.mozAudioContext ||
      window.oAudioContext ||
      window.msAudioContext;

    let ac = null;

    return {
      isSupported() {
        return !!AudioContext;
      },

      get() {
        if (this.isSupported()) {
          if (!ac || ac.state === 'closed') {
            ac = null;
            try {
              ac = new AudioContext();
            } catch (ignore) {
              /* ...*/
            }
          }
        }
        return ac;
      },

      close() {
        if (this.isSupported() && ac) {
          ac.close();
          ac = null;
        }
        return this;
      },
    };
  })(),

  guid: function (id, now) {
    now = now || Date.now();
    return (
      Math.round(Math.random() * now + performance.now() * 1e3).toString(36) +
      '-' +
      now.toString(36) +
      (id ? '-' + String(id).replace(/[^\w\d]/g, '-') : '')
    );
  },

  getHashCode: function (obj) {
    return CryptoJS.MD5(JSON.stringify(obj)).toString();
  },

  prependHttp: function (url) {
    if (!_.isEmpty(url)) {
      if (/^http:/i.test(url)) {
        url = url.replace(/^http:/, '');
      } else if (
        !/^http[s]?:\/\/|^\/\/|^data:|^s3:\/\/|^blob:|^\/resources/i.test(url)
      ) {
        url = '//' + url;
      } else if (/^\/resources\//i.test(url)) {
        url = location.origin + url;
      }
    }
    return url;
  },

  checkAssetEditorUrl(location) {
    const editorPattern = /edit\/(.*)\/(.*)\/(.*)/gi;
    return editorPattern.exec(location);
  },

  prependHttpAcrossObject: function (obj) {
    if (_.isDefined(obj)) {
      const fields = [
        'url',
        'cdnUrl',
        'urlCdn',
        'posterUrl',
        'animatedGifUrl',
        'thumbnail',
        'thumbnail160x90Url',
        'thumbnail256x144Url',
        'hiResAssetUrl',
        'socialUserProfileImageUrl',
        'providerLowResAssetUrl',
      ];
      _.each(
        fields,
        $.proxy((field) => {
          if (Object.getOwnPropertyDescriptor(obj, field)) {
            obj[field] = this.prependHttp(obj[field]);
          }
          if (
            _.isDefined(obj.metadata) &&
            Object.getOwnPropertyDescriptor(obj.metadata, field)
          ) {
            obj.metadata[field] = this.prependHttp(obj.metadata[field]);
          }
        })
      );

      if (_.isDefined(obj.previewResultData)) {
        obj.previewResultData.cdnUrl = this.prependHttp(
          obj.previewResultData.cdnUrl
        );
        obj.previewResultData.thumbnail = this.prependHttp(
          obj.previewResultData.thumbnail
        );
      }
    }
  },

  preloadImage: function (url) {
    if (!_.isEmpty(url)) {
      const img = new Image();
      img.src = url;
    }
  },

  compileTemplate: new (function () {
    const cache = new Map();
    const options = {
      autoremove: true,
    };
    const engines = {
      handlebars: Handlebars.compile,
      _default: Handlebars.compile,
    };

    // this function was modified to disable cache because it was causing issues with the handlebars templates
    // under investigation...
    return {
      $: function (selector, engineType) {
        const engineFn = engines[engineType] || engines._default;

        if (!cache.has(selector)) {
          const $el = $(selector);
          cache.set(selector, engineFn($el.text().normalizeSpace()));
          options.autoremove && $el.remove();
        }
        return cache.get(selector);
        // return engineFn($el.text().normalizeSpace());
      },
      html: function (text, engineType) {
        const engineFn = engines[engineType] || engines._default;
        if (!cache.has(text)) {
          cache.set(text, engineFn(text));
        }
        return cache.get(text);
        // return engineFn(text);
      },
      getCache: function () {
        return cache;
      },
    };
  })(),

  /**
   * Send errors / realtime client issues to backend log
   * msg (String)
   */
  logReport: function (msg) {
    if (!msg) {
      return;
    }
    const promise = $.Deferred();
    $.post(app.apiUrl('/task/report'), {
      msg: msg,
    })
      .done(function (res) {
        if ('OK' === res) {
          promise.resolve();
        } else {
          promise.reject(res);
        }
      })
      .fail(function (res) {
        promise.reject(res);
      });

    promise
      .done(function () {
        console.warn('error pushed successfully to backend log ->', msg);
      })
      .fail(function (res) {
        console.warn("couldn't push backend error log -> ", msg, res);
      });
  },

  xhrExpected: function (xhr) {
    if (
      _.isDefined(xhr) &&
      (!Object.getOwnPropertyDescriptor(xhr, 'readyState') ||
        !_.isNumeric(xhr.readyState))
    ) {
      app.doctorController.report(
        'app.xhrExpected: incorrect API usage, xhr object expected'
      );
      return false;
    }

    return (
      _.isDefined(xhr) &&
      xhr.readyState !== XMLHttpRequest.UNSENT &&
      xhr.readyState !== XMLHttpRequest.DONE
    );
  },

  /**
   * helper to extract files from event depending on its origin
   * @param e {Event}
   * @return {Array|FileList}
   * @private
   */
  getFilesFromEvent: function (e) {
    let files = [];
    if (
      e.dataTransfer &&
      e.dataTransfer.files &&
      e.dataTransfer.files.length > 0
    ) {
      // dnd origin
      files = e.dataTransfer.files;
    } else if (e.target.files && e.target.files.length > 0) {
      // input origin
      files = e.target.files;
    } else if (
      e.clipboardData &&
      e.clipboardData.files &&
      e.clipboardData.files.length > 0
    ) {
      // paste origin
      files = e.clipboardData.files;
    }

    return files;
  },

  generateAssetStashedVolumeKey: function (asset, storyId, voNovo) {
    const hasValidAudio = !!asset.hasValidAudio;
    const assetType = hasValidAudio ? 'HASAUDIO' : 'NOAUDIO';
    return `${storyId}_${voNovo}_${assetType}`;
  },

  getVolumeSettings: function (storyModel) {
    const volumePresets = tk.context.soundControlSettingsPerProgrammer;
    const taskProgrammer = storyModel.get('programmerId');
    const matchedPreset = volumePresets[taskProgrammer];
    if (!matchedPreset) {
      const err =
        'Could not find story programmerId in globalcontext audio presets';
      app.dialog.serverError(err);
      console.warn(err);
    } else {
      return matchedPreset;
    }
  },

  getAppLocation: function () {
    return window.location.hostname + ':' + window.location.port + '/';
  },

  setDefaultAssetItemSchemaModel: function ({
    asset = null,
    task = null,
    isNovoAsset = null,
    replacedAsset = null,
    resetTimelineItemIds = false,
  } = {}) {
    if (resetTimelineItemIds || !asset.timelineItemId) {
      asset.timelineItemId = app.guid();
    }
    if (!asset.assetItemSchemaDataModel) {
      let changes = {
        editable: true,
        mandatory: true,
        removable: true,
        mediaLink: null,
      };
      if (replacedAsset && replacedAsset.assetItemSchemaDataModel) {
        changes = replacedAsset.assetItemSchemaDataModel;
      }
      asset.assetItemSchemaDataModel = changes;
    }

    if (asset.agd && !_.isEmpty(asset.agd.customOverlays)) {
      _.each(asset.agd.customOverlays, (co) => {
        if (!co.timelineItemId || resetTimelineItemIds) {
          co.timelineItemId = app.guid();
        }

        if (co.fields && !_.isEmpty(co.fields)) {
          _.each(co.fields, (coField) => {
            app.setDefaultCustomoverlayFieldDataModel(coField);
          });
        }
      });
    }
  },

  unsetAssetItemSchemaModel: function ({ asset = null }) {
    if (asset && asset.assetItemSchemaDataModel) {
      delete asset.assetItemSchemaDataModel;
    }
    if (asset.agd && !_.isEmpty(asset.agd.customOverlays)) {
      _.each(
        asset.agd.customOverlays.filter(
          ({ fields }) => fields && fields.length
        ),
        (co) => _.each(co.fields, app.unsetCustomoverlayFieldDataModel)
      );
    }
  },

  setDefaultCustomoverlayFieldDataModel: function (coField) {
    if (coField.fieldDataModel) {
      return;
    }

    const storyboardingFlags = coField.defaultStoryboardingFlags || {};

    let linkedFields = null;
    if (coField.type === 'TEXT' && typeof coField.value === 'string') {
      linkedFields = [{ type: 'text', text: coField.value }];
    }

    coField.fieldDataModel = Object.assign(
      {},
      {
        fieldName: coField.name,
        linkedFields: linkedFields,
        mandatory:
          coField.type === 'COMPOUND' &&
          coField.fieldClassification === 'Review',
        editable: true,
        hint: null,
      },
      storyboardingFlags
    );
  },

  unsetCustomoverlayFieldDataModel: function (coField) {
    if (coField.fieldDataModel) {
      delete coField.fieldDataModel;
    }
  },

  setVolumeControl({
    replacedAsset,
    asset,
    story,
    key,
    settings,
    type,
    defaultSoundPresetForVideos,
  } = {}) {
    let selectedPreset;
    const hasValidAudio = !!asset.hasValidAudio;

    if (!asset[key]) {
      if (replacedAsset && hasValidAudio === replacedAsset.hasValidAudio) {
        selectedPreset = replacedAsset[key];
      } else if (hasValidAudio) {
        if (
          _.isNull(defaultSoundPresetForVideos) ||
          defaultSoundPresetForVideos === 'mix'
        ) {
          selectedPreset = settings.mix;
        } else {
          selectedPreset = settings[defaultSoundPresetForVideos];
        }
      } else {
        selectedPreset = settings.bgOnly;
      }
      selectedPreset = _.cloneDeep(selectedPreset);
      const stashedVolumeKey = app.generateAssetStashedVolumeKey(
        asset,
        story.get('id'),
        type
      );
      const stashedVolume =
        app.storyController.story.remoteStash.get(stashedVolumeKey);
      if (stashedVolume) {
        Object.assign(selectedPreset, Object.assign({}, stashedVolume));
      }
    }

    if (selectedPreset) {
      asset[key] = {
        narration: _.isNumber(selectedPreset.narration)
          ? selectedPreset.narration
          : null,
        bgMusic: _.isNumber(selectedPreset.bg)
          ? selectedPreset.bg
          : _.isNumber(selectedPreset.bgMusic)
          ? selectedPreset.bgMusic
          : null,
        video: hasValidAudio ? selectedPreset.video : null,
      };
    }
  },

  setDefaultAssetVolumes: function ({ asset, story, replacedAsset } = {}) {
    const defaultVolumePresets = app.getVolumeSettings(story);
    const {
      novoSettings,
      narrationSettings: voSettings,
      defaultSoundPresetForVideos,
    } = defaultVolumePresets;
    this.setVolumeControl({
      story,
      asset,
      replacedAsset,
      defaultSoundPresetForVideos,
      key: 'volumeControl',
      settings: novoSettings,
      type: 'novo',
    });
    this.setVolumeControl({
      story,
      asset,
      replacedAsset,
      defaultSoundPresetForVideos,
      key: 'onVoPlayVolumeControl',
      settings: voSettings,
      type: 'vo',
    });
  },

  setDefaultAGD: function (story, visualTrack, replacedAsset) {
    try {
      const aspectRatio = story.get('aspectRatio');
      const shouldAutoCrop = !!story.getProgrammer().get('enableMediaAutocrop');

      const applyAutoTransition =
        !!story.get('transitionSelection').autoShuffle &&
        story.getThemeInfo().transitionPresets.length > 0;

      const zoomReset = {
        zoomX: null,
        zoomX1: null,
        zoomY: null,
        zoomY1: null,
        effectType: null,
      };

      if (_.isEmpty(visualTrack.agd)) {
        visualTrack.agd = {};
      }

      if (replacedAsset) {
        const agd = _.cloneDeepAndClean(replacedAsset.agd);
        if (
          app.assetType.isFRAGMENT(visualTrack) ||
          app.assetType.isMAP(visualTrack) ||
          app.assetType.isGIF(visualTrack) ||
          app.assetType.isTWITTER(visualTrack) ||
          app.assetType.isWALLPAPER(visualTrack) ||
          app.assetType.isWEBPAGE(visualTrack) ||
          app.assetType.isWEBPAGE(replacedAsset)
        ) {
          Object.assign(agd, zoomReset);
        }
        if (
          !app.assetType.isFRAGMENT(visualTrack) ||
          !app.assetType.isFRAGMENT(replacedAsset)
        ) {
          agd.videoQuoteSubTitles = null;
        }

        if (!app.assetType.isFRAGMENT(visualTrack)) {
          delete agd.videoSpeed;
        }

        agd.transition = Object.assign({}, replacedAsset.agd.transition);
        delete agd.scale;
        delete agd.offset;

        visualTrack.agd = agd;
      } else {
        if (applyAutoTransition) {
          const selectedTransitionPreset = story.get(
            'transitionSelection'
          ).selectedPreset;
          const transitionsPresetFind = story
            .getThemeInfo()
            .transitionPresets.find(
              (transitionsGroup) =>
                transitionsGroup.name === selectedTransitionPreset
            );

          if (!transitionsPresetFind) {
            app.doctorController.report('no transition group found ');
          } else {
            const pool = transitionsPresetFind.transitions.map(
              (t) => t.wochletName
            );
            visualTrack.agd.transition = {
              wiperType: 'WIPER',
              wiperName: pool[parseInt(Math.random() * pool.length)],
              presetName: selectedTransitionPreset,
              selectedByUser: false,
            };
          }
        } else {
          visualTrack.agd.transition = {
            wiperType: null,
            wiperName: null,
            presetName: null,
            selectedByUser: false,
          };
        }
      }

      if (
        app.assetType.isFRAGMENT(visualTrack) &&
        !visualTrack.agd.videoSpeed
      ) {
        visualTrack.agd.videoSpeed = 1;
      }

      if (!_.isUndefined(visualTrack)) {
        if (app.assetType.isFairUse(visualTrack)) {
          let credits = '';
          if (!_.isUndefined(visualTrack.metadata)) {
            credits = visualTrack.metadata.credits;
          }
          if (!_.isEmpty(credits)) {
            visualTrack.agd.creditPosition = 'UPPER_RIGHT';
            visualTrack.agd.creditText = credits;
          }
        }
      }

      // scale to 16:9 if needed
      if (
        shouldAutoCrop &&
        (app.assetType.isIMAGE(visualTrack) ||
          app.assetType.isFRAGMENT(visualTrack))
      ) {
        // const assetAR = visualTrack.metadata.width / visualTrack.metadata.height;
        // let newWidth;
        // let newHeight;
        // let assetTop;
        // let assetLeft;
        // let isShorterThanEditor;

        // if (assetAR < this.ASPECT_RATIOS[aspectRatio].ratio && assetAR /
        //     this.ASPECT_RATIOS[aspectRatio].ratio >= 0.7) {
        //   newWidth = app.ASSET_EDITOR_HEIGHT *
        //       this.ASPECT_RATIOS[aspectRatio].ratio;
        //   newHeight = visualTrack.metadata.height *
        //       (newWidth / visualTrack.metadata.width);
        //   isShorterThanEditor = newHeight < app.ASSET_EDITOR_HEIGHT;
        //   assetTop = isShorterThanEditor ?
        //     (app.ASSET_EDITOR_HEIGHT / 2 - newHeight / 2) :
        //     0;
        //   assetLeft = (app.ASSET_EDITOR_WIDTH - newWidth) / 2;
        //   visualTrack.agd.scale = newHeight / app.ASSET_EDITOR_HEIGHT;
        //   visualTrack.agd.offset = {
        //     left: assetLeft / app.ASSET_EDITOR_WIDTH,
        //     top: assetTop / app.ASSET_EDITOR_HEIGHT,
        //   };
        // } else {
        //   delete visualTrack.agd.scale;
        //   delete visualTrack.agd.offset;
        // }
        const { scale: autoCropScale, offset: autoCropOffset } =
          new AutoCropHandler()
            .setNext(new CleanScaleAndOffsetHandler())
            .AdjustAsset({
              originalRatio: {
                height: visualTrack.metadata.height,
                width: visualTrack.metadata.width,
              },
              innerRatio: {
                height: app.ASSET_EDITOR_HEIGHT,
                width:
                  app.ASSET_EDITOR_HEIGHT *
                  this.ASPECT_RATIOS[aspectRatio].ratio,
              },
              baseRatio: {
                height: app.ASSET_EDITOR_HEIGHT,
                width: app.ASSET_EDITOR_WIDTH,
              },
            });
        visualTrack.agd.scale = autoCropScale;
        visualTrack.agd.offset = autoCropOffset;
      }

      if (app.assetType.isFRAGMENT(visualTrack)) {
        if (replacedAsset) {
          if (visualTrack.agd.videoSpeed !== 1) {
            const newDuration =
              (visualTrack.endTime - visualTrack.startTime) /
              visualTrack.agd.videoSpeed;
            const newDisplayDuration = app.sfp(newDuration);

            Object.assign(visualTrack, {
              duration: newDuration,
              displayDuration: newDisplayDuration,
              displayEndTime: visualTrack.displayStartTime + newDisplayDuration,
            });
          }
        }
      }

      const transcriptions = app.rootStore.state.story.transcriptions;
      const hisTranscript = transcriptions[visualTrack.metadata.assetUUID];
      const enableVOSubtitles = app.rootStore.state.story.enableVOSubtitles;

      if (!hisTranscript) {
        visualTrack.agd.showTranscription = null;
      } else {
        if (_.isEmpty(hisTranscript.tokens)) {
          visualTrack.agd.showTranscription = null;
        } else {
          visualTrack.agd.showTranscription = enableVOSubtitles;
        }
      }
    } catch (bug) {
      app.report('setDefaultAGD', bug);
    }
  },

  isApiUrl: new (function () {
    let reApiUrl;
    return (url) => {
      if (!reApiUrl) {
        reApiUrl = new RegExp(
          '^' + app.escapeRegExpOperators(serverProfile.apiUrl),
          'i'
        );
      }

      return reApiUrl.test(url);
    };
  })(),

  isAiApiUrl: new (function () {
    let reApiUrl;
    return (url) => {
      if (!reApiUrl) {
        reApiUrl = new RegExp(
          '^' + app.escapeRegExpOperators(serverProfile.aiApiUrl),
          'i'
        );
      }
      return reApiUrl.test(url);
    };
  })(),

  isWochitUrl(url) {
    // check if it's wochit's cdn or S3 url
    return /akamaihd\.net|cloudfront\.net|amazonaws\.com/.test(url);
  },

  isRenderApiUrl: new (function () {
    let reApiUrl;
    return (url) => {
      if (!reApiUrl) {
        reApiUrl = new RegExp(
          '^' + app.escapeRegExpOperators(serverProfile.renderApiUrl),
          'i'
        );
      }

      return reApiUrl.test(url);
    };
  })(),

  ROLES: {
    STORY_CREATION: 'STORY_CREATION',
    VIDEO_RENDERING: 'VIDEO_RENDERING',
    PUBLISH: 'PUBLISH',
  },

  initialize: function () {
    // init Vue plugins
    Vue.use(Vuex); // init Vuex
    Vue.use(AnalyticsPlugin); // init AnalyticsPlugin
    Vue.use(MediaQueryPlugin); // init MediaQueryPlugin

    Vue.use(LiquorTree);

    // if not in prod, enable full vue development mode
    if (serverProfile.environment === 'prod') {
      Vue.config.silent = true;
      Vue.config.devtools = false;
      Vue.config.performance = false;
      Vue.config.productionTip = false;
    }

    const shouldDeflate =
      serverProfile.environment !== 'dev' && localStorage.debug !== 'true';
    const globalAjaxOptions = {
      deflate: shouldDeflate,
      xhrFields: {
        // send auth cookies to cross domain api server
        withCredentials: true,
      },
    };

    $.ajaxSetup({
      beforeSend: function (xhr) {
        const isRenderApi = xhr.requestUrl.includes(serverProfile.renderApiUrl);
        const isWochitApi =
          xhr.requestUrl.includes(serverProfile.apiUrl) ||
          xhr.requestUrl.includes(serverProfile.authUrl) ||
          xhr.requestUrl.includes(serverProfile.aiApiUrl) ||
          isRenderApi;

        if (isWochitApi) {
          const channelId = tk.context.channel.id;
          const AuthorizationHeader = sessionStorage.getItem('Authorization');

          if (AuthorizationHeader) {
            xhr.setRequestHeader('Authorization', AuthorizationHeader);
          }

          if (channelId) {
            xhr.setRequestHeader('Channel-ID', channelId);
          }

          const regorg = /^.*\?.*orgId=(\d+).*$/i;
          const [, orgId] = xhr.requestUrl.match(regorg) || [];

          const organizationId =
            orgId || localStorage.getItem('selectedOrganizationId');
          if (organizationId && !isRenderApi) {
            xhr.setRequestHeader('Organization-Id', organizationId);
          }
        }
      },
      statusCode: {
        /** LOGGED OUT */
        401: function (jqXHR) {
          if (
            jqXHR.requestUrl &&
            // from those social-connect url's the 401 code has external meaning
            !/\/stories\/.*\/video\/social/i.test(jqXHR.requestUrl) &&
            !/\/users\/me\/social/i.test(jqXHR.requestUrl) &&
            !/\/assets\/search\/social/i.test(jqXHR.requestUrl)
          ) {
            app.delayedSpinner.hide();
            if (app.connectionController) {
              app.connectionController.needToReauthenticate();
            }
          }
        },

        /** LOCKED */
        423: function () {
          // without sentiments
          app.rootStore.dispatch('user/logout');
        },
      },
    });

    $.ajaxPrefilter((options, originalOptions, jqXHR) => {
      // extend jqXHR with requested url
      jqXHR.requestUrl = options.url;

      const isRenderApiUrl = this.isRenderApiUrl(options.url);
      const isApiUrl = this.isApiUrl(options.url);
      const isAiApiUrl = this.isAiApiUrl(options.url);

      if (!isApiUrl && !isRenderApiUrl && !isAiApiUrl) {
        return;
      }

      // @note: allowing to custom ajax options to override the global options
      const mergedOptions = $.extend(
        true,
        {},
        globalAjaxOptions,
        _.pick(originalOptions, _.keys(globalAjaxOptions))
      );
      $.extend(options, mergedOptions);
      if (
        isApiUrl &&
        options.deflate &&
        options.contentType === 'application/json' &&
        typeof originalOptions.data === 'string'
      ) {
        jqXHR.setRequestHeader('X-Wochit-Content-Encoding', 'deflate');
        // @note overriding json string with binary array
        options.data = this.pako.deflate(originalOptions.data);
      }
    });
  },

  generateAssetRealTimeChecksum: function (options) {
    function adgJSONStringify(agd) {
      agd = app.compactAndSortObject(agd);
      app.unsetAssetItemSchemaModel({ asset: { agd } });

      return JSON.stringify(agd, function (key, value) {
        return !value ||
          key === 'html' ||
          key === 'timelineItemId' ||
          /^[_|$]/.test(key)
          ? undefined
          : value;
      });
    }

    function overlayTrackToString(overlay) {
      let rv = [
        overlay.id,
        adgJSONStringify(overlay.agd),
        adgJSONStringify(overlay.sceneAgd),
        overlay.sceneLayout.id,
        overlay.sceneLayout.switchMainAndOverlay || '',
        overlay.sceneLayout.assetBorder
          ? overlay.sceneLayout.assetBorder.id || ''
          : '',
        overlay.sceneLayout.inAnimationId,
        overlay.sceneLayout.outAnimationId,
        overlay.wallpaperId || overlay.colorName,
      ];

      const aDisplay = Object.keys(overlay).filter((key) =>
        /^display.*$/.test(key)
      );
      const asDisplay = aDisplay.map((key) => `${key}_${overlay[key]}`).sort();
      rv = rv.concat(asDisplay);

      return rv.join(',');
    }

    function overlayTracksToString(overlayTracks = []) {
      return overlayTracks
        .map((overlay, i) => overlayTrackToString(overlay))
        .join('_');
    }

    options = $.extend(
      {
        visualTrack: {},
        aspectRatio: null,
        type: null,
        verbose: false,
      },
      options
    );

    const tmpAGD = $.extend({}, _.cloneDeep(options.visualTrack.agd));

    if (tmpAGD.customOverlays) {
      const firstCo = tmpAGD.customOverlays[0];
      if (options.noInstantPreviewFieldsObject && firstCo?.fields?.length) {
        firstCo.fields = firstCo.fields.filter(
          (field) => options.noInstantPreviewFieldsObject[field.name] !== true
        );
      }

      const forRTP = options.type !== app.RealtimePreviewType.STATIC_MASK;
      if (forRTP) {
        tmpAGD.customOverlays = _.filter(
          tmpAGD.customOverlays,
          (co) => !co.noRealtimePreview
        );
        if (tmpAGD.customOverlays.length === 0) {
          tmpAGD.customOverlays = null;
        }
      }
    }

    _.forEach(this.REALTIME_IGNORE_FIELDS, function (value) {
      delete tmpAGD[value];
    });

    let colorStr = '';

    if (options.themeOverrides) {
      const { themeVariantOverrides: colors } = options.themeOverrides;
      colorStr = colors.reduce((acc, el) => {
        if (el.value) {
          return acc + el.value;
        }
      }, '');
    }

    // (!) - DO NOT CHANGE THE ORDER OF THIS CHECKSUM - (!)
    const calc =
      options.visualTrack.id +
      (options.visualTrack.metadata.assetType === 'WALLPAPER'
        ? options.visualTrack.wallpaperId
        : '') +
      options.visualTrack.displayDuration +
      options.aspectRatio +
      colorStr +
      overlayTracksToString(options.visualTrack.overlayTracks) +
      adgJSONStringify(tmpAGD) +
      (JSON.stringify(options.storyCustomOverlays || []) +
        app.storyController.story?.get('storyLanguage'));

    const calcHash = CryptoJS.MD5(calc).toString();

    if (options.verbose) {
      console.diff?.(calc);
      console.debug(calc);
      console.debug(calcHash);
    }

    return calcHash;
  },

  compactAndSortObject: function (o, options) {
    let rv;
    const usedValues = new Set();
    options = $.extend(
      {
        ignoreEmptyArrays: false,
        ignoreEmptyObjects: false,
        ignoreZeros: false,
      },
      options
    );

    function determineIgnores(value) {
      if (!_.isDefined(value)) {
        return false;
      } else if (
        options.ignoreEmptyObjects &&
        _.isObject(value) &&
        _.isEmpty(value)
      ) {
        return false;
      } else if (
        options.ignoreEmptyArrays &&
        _.isArray(value) &&
        _.isEmpty(value)
      ) {
        return false;
      } else if (options.ignoreZeros && value === 0) {
        return false;
      }
      return value !== '';
    }

    if (o instanceof Object) {
      if (
        o instanceof Element ||
        o instanceof Backbone.View ||
        usedValues.has(o)
      ) {
        rv = o;
        return rv;
      } else {
        usedValues.add(o);
      }
    }

    if (o instanceof Array) {
      rv = [];
      _.each(o, (value) => {
        determineIgnores(value, options);
        if (determineIgnores(value)) {
          if (value instanceof Object) {
            value = this.compactAndSortObject(value, options);
          }
          rv.push(value);
        }
      });
    } else if (o instanceof Object) {
      const keys = _.keys(o);
      keys.sort();
      rv = {};
      _.each(keys, (key) => {
        let value = o[key];
        if (determineIgnores(value)) {
          if (value instanceof Object) {
            value = this.compactAndSortObject(value, options);
          }
          rv[key] = value;
        }
      });
    } else {
      rv = o;
    }

    return rv;
  },

  /**
   * Log positive count of pings each second for debugging purposes
   * @param name
   * @constructor
   */
  CallRatioPerSec: function (name) {
    this.r = 0;

    this.ping = function () {
      ++this.r;
    };

    this.reset = function () {
      this.r = 0;
    };

    this.start = function () {
      this.stop();
      this.t = window.setInterval(
        function () {
          if (this.r > 0) {
            console.debug('CallRatioPerSec:', name || '', this.r);
            this.reset();
          }
        }.bind(this),
        1e3
      );
    };

    this.stop = function () {
      this.t = window.clearInterval(this.t);
    };
  },

  resetVisualTrackRTP: function (visualTrack) {
    if (
      visualTrack._realTimePreviewInProgress &&
      visualTrack._tmpRealTimePreviewChecksum
    ) {
      app.realTimePreviewController.abortRTP(
        visualTrack._tmpRealTimePreviewChecksum
      );
    }
    const removeOldFields = [
      'realTimePreviewChecksum',
      'realTimePreviewResultData',
      '_realTimePreviewInProgress',
      '_tmpRealTimePreviewChecksum',
    ];
    _.each(
      removeOldFields,
      function (oldField) {
        visualTrack[oldField] = null;
      },
      this
    );
  },

  showModal: function (options) {
    options = Object.assign(
      {
        // all options for wt-base-modal has defaults define in props
        appendIdentifier: '#wtModal',
        analytics: this.analytics,
        store: undefined,
        router: undefined,
      },
      options || {}
    );

    const BaseModal = Vue.extend({
      functional: true,
      store: options.store,
      router: options.router,

      render(createElement) {
        return createElement('wt-base-modal', {
          // vue filter out props that not defined in modal props (like appendIdentifier)
          props: _.omit(options, ['appendIdentifier', 'store', 'router']),
        });
      },
    });

    const kaName = `MODAL_${this.guid(options?.component.name)}`;
    const isMac = navigator.platform === 'MacIntel';
    const metaProp = isMac ? 'metaKey' : 'ctrlKey';
    const handlers = [
      // prevent timeline zoom
      { on: 'keyup', key: 187 /* plus*/, run: _.noop },
      { on: 'keyup', key: 189 /* minus*/, run: _.noop },
      // prevent undo-redo
      { on: 'keydown', key: 90 /* Z*/, run: _.noop, [metaProp]: true },
      {
        on: 'keydown',
        key: 90 /* Z*/,
        run: _.noop,
        [metaProp]: true,
        shiftKey: true,
      },
    ];
    if (!isMac) {
      handlers.push({
        on: 'keydown',
        key: 89 /* Y*/,
        run: _.noop,
        ctrlKey: true,
      });
    }
    app.keyboardAccessibility.add(kaName, handlers);

    const modal = new BaseModal().$mount();
    const modalEl =
      document.querySelector(options.appendIdentifier) ||
      document.querySelector('body');
    modalEl.appendChild(modal.$el);

    modal.$on('close', () => {
      app.keyboardAccessibility.remove(kaName);
    });

    // return modal instance
    return modal;
  },

  /**
   * Close all or only selected modals opened with `app.showModal`
   * @param [arrClass] {string[]} - selectors to close
   */
  closeModals(arrClass = []) {
    document.querySelectorAll('.wt2-modal').forEach((el) => {
      const vue = el.__vue__;
      let close = !!vue;

      if (close && arrClass.length) {
        const found = arrClass.find((className) =>
          el.classList.contains(className)
        );
        if (!found) {
          close = false;
        }
      }

      if (close) {
        vue.$destroy();
        el.__vue__ = null;
        el.remove();
      }
    });
  },

  /**
   * @param e {Event}
   * @return {Boolean}
   */
  abortKeyboard: function (e) {
    return (
      app.delayedSpinner.isVisible() ||
      $(document).find('.wt2-modal:visible').length ||
      $(document).find('.modal:visible').length ||
      $(e.target).isEditable()
    );
  },

  log(message, color) {
    console.log(`%c${message}`, `color: ${color}; font-weight: bold;`);
  },

  computeBgFromColorData(colorData) {
    // solid color
    if (colorData.colorValue) {
      return { background: `${colorData.colorValue}` };
    } else if (colorData.gradientType === app.gradientTypes.LINEAR) {
      // linear-gradient
      return {
        background: `linear-gradient(${colorData.angle}deg, ${colorData.gradientColors[0].color} ${colorData.gradientColors[0].position}%, ${colorData.gradientColors[1].color} ${colorData.gradientColors[1].position}%)`,
      };
    } else if (colorData.gradientType === app.gradientTypes.RADIAL) {
      // radial-gradient
      return {
        background: `radial-gradient(circle at center center, ${colorData.gradientColors[0].color} ${colorData.gradientColors[0].position}%, ${colorData.gradientColors[1].color} ${colorData.gradientColors[1].position}%)`,
      };
    }

    return {};
  },

  getRgbaString(color) {
    const tiny = tinycolor(color);
    const { r, g, b, a } = tiny.toRgb();
    return `rgba(${r},${g},${b},${a})`;
  },

  getHexString(color) {
    const tiny = tinycolor(color);
    return tiny.toHexString();
  },

  cssObjectToString(cssObj) {
    const rv = [];
    _.each(cssObj, (value, key) => {
      rv.push(`${key}:${value};`);
    });
    return rv.join('');
  },

  cssVarValue(el, varName) {
    if (!_.isElement(el)) {
      return null;
    }
    const computedStyle = window.getComputedStyle(el);
    return computedStyle.getPropertyValue(varName);
  },

  timeout(ms) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, ms);
    });
  },

  trk(i = 0, j = 0) {
    // Just for debugging. returns the sceneLayout of overlayTrack j in visual track i
    return window.story.attributes.timelineData.visualTracks[i].overlayTracks[
      j
    ];
  },

  getMainLayout(sceneLayout) {
    return sceneLayout.switchMainAndOverlay
      ? sceneLayout.overlayAssetBoundingBox
      : sceneLayout.mainAssetBoundingBox;
  },
  getOverlayLayout(sceneLayout) {
    return sceneLayout.switchMainAndOverlay
      ? sceneLayout.mainAssetBoundingBox
      : sceneLayout.overlayAssetBoundingBox;
  },
  _rcps: {},
  setCommonRCPVolume(rcp) {
    const volume = this.storyController.story.remoteStash.get('rcpVolume');
    const { setVolume, guid } = rcp;
    this._rcps[rcp.guid] = {
      setVolume,
      guid,
    };
    rcp.setVolume(_.isNumber(volume) ? volume : 1);
    rcp.on(rcp.constructor.MSG.VOLUMECHANGE, (volume) => {
      this.storyController.story.remoteStash.set('rcpVolume', volume);
      Object.keys(this._rcps)
        .filter((guid) => guid !== rcp.guid)
        .forEach((guid) => {
          this._rcps[guid].setVolume(volume);
        });
    });
    rcp.on(rcp.constructor.MSG.DESTROY, (guid) => {
      delete this._rcps[guid];
    });
  },

  getOverlayLayoutAssetType(type) {
    switch (type) {
      case this.assetType.FRAGMENT:
      case this.assetType.SOCIALFRAGMENT: {
        return this.assetType.VIDEO;
      }
      case this.assetType.WALLPAPER: {
        return this.assetType.IMAGE;
      }
      default: {
        return type;
      }
    }
  },
  getWidthHeight(metadata) {
    if (metadata.width) {
      return Promise.resolve(metadata);
    }
    return new Promise((res) => {
      const el = document.createElement(
        metadata.url && metadata.url.match(/\.mp4\b/i) ? 'video' : 'img'
      );
      el.setAttribute('crossOrigin', 'anonymous');
      el.oncanplay = (e) => {
        res({
          ...metadata,
          width: e.target.videoWidth,
          height: e.target.videoHeight,
        });
      };
      el.onload = (e) => {
        res({
          ...metadata,
          width: e.target.width,
          height: e.target.height,
        });
      };
      el.onerror = (e) => {
        console.log(e);
      };
      if (
        !metadata.url &&
        metadata.providerId.match(
          new RegExp('^' + app.PROVIDERS.TWITTER.id + '$', 'i')
        )
      ) {
        el.src = app.TWITTER_PLACEHOLDER.URL_BIG;
      } else {
        if (metadata.url.startsWith('http')) {
          el.src = metadata.url;
        } else {
          el.src = location.protocol + metadata.url;
        }
      }
    });
  },
  getSceneLayoutObject() {
    const remoteStash = this.storyController.story?.remoteStash;
    return JSON.parse(
      remoteStash ? remoteStash.get('SCENE_LAYOUT_STRING') || '{}' : '{}'
    );
  },
  setSceneLayoutObject(sceneLayoutObject) {
    this.storyController.story.remoteStash.set(
      'SCENE_LAYOUT_STRING',
      JSON.stringify(sceneLayoutObject || {})
    );
  },
  collageSupport({ action }) {
    app.analytics('clickedCollageSupport', action);
    app.openSupportTab({ path: 'collage' })?.focus();
  },
  handleFaceOnCameraResponse(options = {}) {
    const data = Object.assign({
      orgId: localStorage.getItem('selectedOrganizationId'),
      privacy: app.privacyType.USER_PRIVATE,
      keywords: null,
      attributionMode: app.attributionType.NONE,
      title:
        'Video Recorder - ' + app.moment().format('MMMM Do YYYY, h:mm:ss A'),
      fileName: 'tmp.webm',
      metadata: {
        assetUUID: app.guid(),
        uploadStatus: app.uploadStatus.UPLOAD,
        caption: '',
        transcript: options.caption,
      },
      progress: {},
      blob: options.blob,
      mimeType: options.blob.type,
      autoTranscribe: options.autoTranscribe,
      _shouldNormalizeVideo: true,
    });
    const model = new app.namespace.Model.UploadQueueModel(data);
    app.UploadQueue.upload(model);
    app.rootStore.dispatch('ESCORT_UPLOADING_ASSET', {
      uploadDestination: options.isLightbox
        ? app.uploadDestination.LIGHTBOX
        : app.uploadDestination.ASSET_GALLERY,
      asset: model.attributes,
    });
  },
  handleNarrationRecordResponse(options = {}) {
    const fileName = options.title || 'Audio Recorder';
    const timeStamp = app.moment().format('MMMM Do YYYY, h:mm:ss A');
    const data = Object.assign({
      orgId: localStorage.getItem('selectedOrganizationId'),
      privacy: app.privacyType.USER_PRIVATE,
      keywords: null,
      attributionMode: app.attributionType.NONE,
      title: `${fileName} - ${timeStamp}.webm`,
      fileName: 'tmp.webm',
      metadata: {
        assetUUID: app.guid(),
        uploadStatus: app.uploadStatus.UPLOAD,
        caption: '',
        transcript: options.caption,
      },
      progress: {},
      blob: options.blob,
      mimeType: options.blob.type,
      mediaAssetSubType: app.mediaAssetSubType.NARRATION,
      type: app.assetType.AUDIO,
    });
    const model = new app.namespace.Model.UploadQueueModel(
      data,
      options.modelProps
    );
    app.UploadQueue.upload(model);
    app.rootStore.dispatch('ESCORT_UPLOADING_ASSET', {
      uploadDestination: options.isLightbox
        ? app.uploadDestination.LIGHTBOX
        : app.uploadDestination.ASSET_GALLERY,
      isNarration: model.options.uploadType === app.uploadType.NARRATION_ASSET,
      asset: model.attributes,
    });
  },

  transcriptionLanguageList() {
    return [
      {
        id: 'af',
        label: 'Afrikaans',
      },
      {
        id: 'sq',
        label: 'Albanian',
      },
      {
        id: 'am',
        label: 'Amharic',
      },
      {
        id: 'ar',
        label: 'Arabic',
      },
      {
        id: 'ar-MSA',
        label: 'Arabic (Modern Standard)',
      },
      {
        id: 'hy',
        label: 'Armenian',
      },
      {
        id: 'az',
        label: 'Azerbaijani',
      },
      {
        id: 'bn',
        label: 'Bengali',
      },
      {
        id: 'bs',
        label: 'Bosnian',
      },
      {
        id: 'bg',
        label: 'Bulgarian',
      },
      {
        id: 'ca',
        label: 'Catalan',
      },
      // {
      // id: 'zh',
      // label: 'Chinese (Simplified)',
      // },
      // {
      // id: 'zh-TW',
      // label: 'Chinese (Traditional)',
      // },
      {
        id: 'hr',
        label: 'Croatian',
      },
      {
        id: 'cs',
        label: 'Czech',
      },
      {
        id: 'da',
        label: 'Danish',
      },
      {
        id: 'fa-AF',
        label: 'Dari',
      },
      {
        id: 'nl',
        label: 'Dutch',
      },
      {
        id: 'en',
        label: 'English',
      },
      {
        id: 'et',
        label: 'Estonian',
      },
      {
        id: 'fa',
        label: 'Farsi (Persian)',
      },
      {
        id: 'tl',
        label: 'Filipino, Tagalog',
      },
      {
        id: 'fi',
        label: 'Finnish',
      },
      {
        id: 'fr',
        label: 'French',
      },
      {
        id: 'fr-CA',
        label: 'French (Canada)',
      },
      {
        id: 'ka',
        label: 'Georgian',
      },
      {
        id: 'de',
        label: 'German',
      },
      {
        id: 'el',
        label: 'Greek',
      },
      {
        id: 'gu',
        label: 'Gujarati',
      },
      {
        id: 'ht',
        label: 'Haitian Creole',
      },
      {
        id: 'ha',
        label: 'Hausa',
      },
      {
        id: 'he',
        label: 'Hebrew',
      },
      {
        id: 'hi',
        label: 'Hindi',
      },
      {
        id: 'hu',
        label: 'Hungarian',
      },
      {
        id: 'is',
        label: 'Icelandic',
      },
      {
        id: 'id',
        label: 'Indonesian',
      },
      {
        id: 'ga',
        label: 'Irish',
      },
      {
        id: 'it',
        label: 'Italian',
      },
      // {
      //   id: 'ja',
      //   label: 'Japanese',
      // },
      {
        id: 'kn',
        label: 'Kannada',
      },
      {
        id: 'kk',
        label: 'Kazakh',
      },
      {
        id: 'ko',
        label: 'Korean',
      },
      {
        id: 'lv',
        label: 'Latvian',
      },
      {
        id: 'lt',
        label: 'Lithuanian',
      },
      {
        id: 'mk',
        label: 'Macedonian',
      },
      {
        id: 'ms',
        label: 'Malay',
      },
      {
        id: 'ml',
        label: 'Malayalam',
      },
      {
        id: 'mt',
        label: 'Maltese',
      },
      {
        id: 'mr',
        label: 'Marathi',
      },
      {
        id: 'mn',
        label: 'Mongolian',
      },
      {
        id: 'no',
        label: 'Norwegian (Bokmål)',
      },
      {
        id: 'ps',
        label: 'Pashto',
      },
      {
        id: 'pl',
        label: 'Polish',
      },
      {
        id: 'pt',
        label: 'Portuguese (Brazil)',
      },
      {
        id: 'pt-PT',
        label: 'Portuguese (Portugal)',
      },
      {
        id: 'pa',
        label: 'Punjabi',
      },
      {
        id: 'ro',
        label: 'Romanian',
      },
      {
        id: 'ru',
        label: 'Russian',
      },
      {
        id: 'sr',
        label: 'Serbian',
      },
      {
        id: 'si',
        label: 'Sinhala',
      },
      {
        id: 'sk',
        label: 'Slovak',
      },
      {
        id: 'sl',
        label: 'Slovenian',
      },
      {
        id: 'so',
        label: 'Somali',
      },
      {
        id: 'es',
        label: 'Spanish',
      },
      {
        id: 'es-MX',
        label: 'Spanish (Mexico)',
      },
      {
        id: 'sw',
        label: 'Swahili',
      },
      {
        id: 'sv',
        label: 'Swedish',
      },
      {
        id: 'ta',
        label: 'Tamil',
      },
      {
        id: 'te',
        label: 'Telugu',
      },
      {
        id: 'th',
        label: 'Thai',
      },
      {
        id: 'tr',
        label: 'Turkish',
      },
      {
        id: 'uk',
        label: 'Ukrainian',
      },
      {
        id: 'ur',
        label: 'Urdu',
      },
      {
        id: 'uz',
        label: 'Uzbek',
      },
      {
        id: 'vi',
        label: 'Vietnamese',
      },
      {
        id: 'cy',
        label: 'Welsh',
      },
    ];
  },
});

export default Application;
